import React from 'react';
import {
  Box,
  Typography,
  Button,
  Stack,
  Chip
} from '@mui/material';
import {
  List as ListIcon,
  Map as MapIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import FiltersSection from './FiltersSection';
import { TotalCountBadge } from '../../../components/badges';
import { PrimaryButton } from '../../../components/buttons';

interface StatusTab {
  id: string;
  label: string;
  count: number;
}

interface PageHeaderProps {
  totalDrivers?: number;
  statusTabs?: StatusTab[];
  selectedStatus?: string;
  onStatusChange?: (status: string) => void;
  onRegisterDriver?: () => void;
  viewMode?: 'cards' | 'map';
  setViewMode?: (mode: 'cards' | 'map') => void;
  // FiltersSection props
  searchTerm: string;
  setSearchTerm: (term: string) => void;
  statusFilter: string | string[];
  setStatusFilter: (status: string | string[]) => void;
  countryFilter: string | string[];
  setCountryFilter: (country: string | string[]) => void;
  certificationFilter: string | string[];
  setCertificationFilter: (certification: string | string[]) => void;
  countries: string[];
  certifications: string[];
}

const PageHeader: React.FC<PageHeaderProps> = ({
  totalDrivers = 0,
  statusTabs = [
    { id: 'all', label: 'All', count: 0 },
    { id: 'active', label: 'Active', count: 0 },
    { id: 'inactive', label: 'Inactive', count: 0 }
  ],
  selectedStatus = 'all',
  onStatusChange,
  onRegisterDriver,
  viewMode = 'cards',
  setViewMode,
  searchTerm,
  setSearchTerm,
  statusFilter,
  setStatusFilter,
  countryFilter,
  setCountryFilter,
  certificationFilter,
  setCertificationFilter,
  countries,
  certifications
}) => {
  const { t } = useTranslation();

  return (
    <Box sx={styles.container}>
      {/* Top Section: Title and Add Button */}
      <Box sx={styles.topSection}>
        <Box sx={styles.titleSection}>
          <Typography variant="h3" sx={styles.title}>
            {t('Drivers')}
          </Typography>
          <TotalCountBadge count={totalDrivers} />
        </Box>
        <Stack direction="row" spacing={1.5} sx={styles.buttonsContainer}>
          {setViewMode && (
            <Box sx={styles.viewToggle}>
              <Button
                variant={viewMode === 'cards' ? 'contained' : 'outlined'}
                size="small"
                onClick={() => setViewMode('cards')}
                startIcon={<ListIcon />}
                sx={styles.viewButton()}
              >
                {t('Cards')}
              </Button>
              <Button
                variant={viewMode === 'map' ? 'contained' : 'outlined'}
                size="small"
                onClick={() => setViewMode('map')}
                startIcon={<MapIcon />}
                sx={styles.viewButton()}
              >
                {t('Map')}
              </Button>
            </Box>
          )}
          {onRegisterDriver && (
            <PrimaryButton
              onClick={onRegisterDriver}
            >
              {t('Create')}
            </PrimaryButton>
          )}
        </Stack>
      </Box>

      {/* Status Tabs */}
      {statusTabs && statusTabs.length > 0 && (
        <Box sx={styles.statusTabsContainer}>
          {statusTabs.map((tab) => {
            const isSelected = selectedStatus === tab.id;
            return (
              <Box
                key={tab.id}
                component="button"
                onClick={() => onStatusChange?.(tab.id)}
                sx={{
                  ...styles.statusTab,
                  ...(isSelected ? styles.statusTabSelected : styles.statusTabUnselected)
                }}
              >
                <Typography sx={styles.statusTabLabel(isSelected)}>
                  {t(tab.label)}
                </Typography>
                <Chip
                  label={tab.count}
                  size="small"
                  sx={styles.statusBadge(isSelected)}
                />
              </Box>
            );
          })}
        </Box>
      )}

      {/* Filters Section */}
      <FiltersSection
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        statusFilter={statusFilter}
        setStatusFilter={setStatusFilter}
        countryFilter={countryFilter}
        setCountryFilter={setCountryFilter}
        certificationFilter={certificationFilter}
        setCertificationFilter={setCertificationFilter}
        countries={countries}
        certifications={certifications}
      />
    </Box>
  );
};

const styles = {
  container: {
    width: '100%',
    mb: { xs: 2, sm: 2.5 }
  },
  topSection: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: { xs: 'flex-start', sm: 'center' },
    mb: 2,
    flexDirection: { xs: 'column', sm: 'row' },
    gap: { xs: 1.5, sm: 2 }
  },
  titleSection: {
    display: 'flex',
    alignItems: 'center',
    gap: 1.5,
    flexWrap: 'wrap'
  },
  title: {
    fontSize: { xs: '1.75rem', sm: '2rem', md: '2.25rem' },
    fontWeight: 600,
    lineHeight: 1.2,
    color: 'text.dark'
  },
  driverCount: {
    fontSize: { xs: '0.875rem', sm: '1rem' },
    color: 'text.grey',
    fontWeight: 500
  },
  statusTabsContainer: {
    display: 'flex',
    gap: 1,
    mb: 2,
    flexWrap: 'wrap',
    overflowX: { xs: 'auto', sm: 'visible' },
    pb: { xs: 0.5, sm: 0 },
    '&::-webkit-scrollbar': {
      height: '4px'
    },
    '&::-webkit-scrollbar-track': {
      backgroundColor: 'background.grey'
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: 'border.medium',
      borderRadius: '2px'
    }
  },
  statusTab: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 1,
    px: { xs: 2, sm: 2.5, md: 3 },
    py: { xs: 0.75, sm: 1 },
    borderRadius: 2,
    border: '1.5px solid',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    whiteSpace: 'nowrap',
    minWidth: { xs: '120px', sm: '140px', md: '160px' }
  },
  statusTabSelected: {
    backgroundColor: (theme: any) => theme.palette.text.purple || '#667eea',
    borderColor: (theme: any) => theme.palette.text.purple || '#667eea',
    boxShadow: (theme: any) => `0 2px 8px ${theme.palette.border.purpleMedium}`
  },
  statusTabUnselected: {
    backgroundColor: 'background.white',
    borderColor: 'border.default',
    '&:hover': {
      borderColor: 'border.dark',
      backgroundColor: 'background.grey'
    }
  },
  statusTabLabel: (isSelected: boolean) => ({
    fontSize: { xs: '0.875rem', sm: '0.9375rem' },
    fontWeight: isSelected ? 600 : 500,
    color: isSelected ? 'text.white' : 'text.grey'
  }),
  statusBadge: (isSelected: boolean) => ({
    height: 24,
    fontSize: '0.75rem',
    fontWeight: 600,
    borderRadius: 1,
    backgroundColor: isSelected
      ? 'background.whiteMedium'
      : 'background.grey',
    color: isSelected ? 'text.white' : 'text.grey',
    '& .MuiChip-label': {
      px: 1.25,
      py: 0
    }
  }),
  buttonsContainer: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: { xs: 1, sm: 1.5 },
    width: { xs: '100%', sm: 'auto' },
    justifyContent: { xs: 'flex-start', sm: 'flex-end' }
  },
  viewToggle: {
    display: 'flex',
    gap: { xs: 0.25, sm: 0.5 },
    background: (theme: any) => theme.palette.mode === 'dark'
      ? theme.palette.background.whiteLight
      : theme.palette.background.white,
    borderRadius: 2,
    p: { xs: 0.25, sm: 0.5 },
    border: (theme: any) => theme.palette.mode === 'light'
      ? `1px solid ${theme.palette.border.darkBorder10}`
      : 'none'
  },
  viewButton: () => ({
    minWidth: 'auto',
    px: { xs: 1, sm: 1.5 },
    py: { xs: 0.5, sm: 0.625 },
    borderRadius: 2,
    fontSize: { xs: '0.75rem', sm: '0.8125rem' },
    textTransform: 'none',
    fontWeight: 600,
    transition: 'all 0.2s ease',
    '&:focus-visible': {
      outline: 'none',
      boxShadow: (theme: any) => theme.palette.mode === 'dark'
        ? `0 0 0 3px ${theme.palette.border.purpleFocusDark}`
        : `0 0 0 3px ${theme.palette.border.purpleFocusLight}`
    },
    '& .MuiButton-startIcon': {
      marginRight: { xs: 0.5, sm: 0.625 },
      '& > *:nth-of-type(1)': {
        fontSize: { xs: '0.9375rem', sm: '1.0625rem' }
      }
    },
    '&.MuiButton-contained': {
      backgroundColor: 'primary.main',
      color: 'primary.contrastText',
      boxShadow: 'none',
      '&:hover': {
        backgroundColor: 'primary.dark',
      },
      '&:active': {
        backgroundColor: 'primary.dark',
      }
    },
    '&.MuiButton-outlined': {
      borderColor: (theme: any) => theme.palette.mode === 'dark'
        ? theme.palette.border.whiteLight
        : theme.palette.border.darkBorder10,
      color: (theme: any) => theme.palette.mode === 'dark'
        ? theme.palette.background.whiteText85
        : theme.palette.background.darkText75,
      backgroundColor: (theme: any) => theme.palette.mode === 'dark'
        ? theme.palette.background.whiteLight
        : theme.palette.background.white,
      '&:hover': {
        backgroundColor: (theme: any) => theme.palette.mode === 'dark'
          ? theme.palette.background.whiteMedium
          : theme.palette.background.darkHover3
      },
      '&:active': {
        backgroundColor: (theme: any) => theme.palette.mode === 'dark'
          ? theme.palette.background.whiteBgPressed
          : theme.palette.background.darkPressed6
      }
    }
  }),
  addButton: () => ({
    px: { xs: 1, sm: 1.5 },
    py: { xs: 0.5, sm: 0.625 },
    borderRadius: 2,
    backgroundColor: (theme: any) => theme.palette.mode === 'dark'
      ? theme.palette.background.purpleHover
      : theme.palette.background.purpleDark,
    color: (theme: any) => theme.palette.mode === 'dark' ? 'text.white' : (theme.palette.text.purple || '#8B5CF6'),
    fontWeight: 600,
    textTransform: 'none',
    fontSize: { xs: '0.75rem', sm: '0.8125rem' },
    boxShadow: (theme: any) => theme.palette.mode === 'dark'
      ? `0 6px 18px ${theme.palette.border.purpleShadowDark}`
      : `0 8px 20px ${theme.palette.border.purpleShadowLight}`,
    transition: 'all 0.2s ease',
    '&:focus-visible': {
      outline: 'none',
      boxShadow: (theme: any) => theme.palette.mode === 'dark'
        ? `0 0 0 3px ${theme.palette.border.purpleFocusDark}`
        : `0 0 0 3px ${theme.palette.border.purpleFocusLight}`
    },
    '& .MuiButton-startIcon': {
      marginRight: { xs: 0.5, sm: 0.625 },
      '& > *:nth-of-type(1)': {
        fontSize: { xs: '0.9375rem', sm: '1.0625rem' }
      }
    },
    '&:hover': {
      backgroundColor: (theme: any) => theme.palette.mode === 'dark'
        ? theme.palette.background.purple
        : theme.palette.background.purpleMedium,
      transform: 'translateY(-1px)'
    },
    '&:active': {
      backgroundColor: (theme: any) => theme.palette.mode === 'dark'
        ? theme.palette.background.purplePressed
        : theme.palette.background.purpleSelectedBg,
      transform: 'translateY(1px)'
    }
  })
};

export default PageHeader;
